import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraEyebrow, SpecraSearchField } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ChevronRight, History, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { formatRelativeTime } from '../../utils/time';
import { chatHref } from '../ChatHistory/chatHref';
import { useChatHistory } from '../ChatHistory/useChatHistory';
import { useSharedWithMe } from '../ChatHistory/useSharedWithMe';
// Persistent left rail for the /assistant surface: recent chats + search + New
// chat. This is the ⌘K switcher's logic (useChatHistory + filter/sort/select)
// surfaced as an always-visible panel instead of a modal; ⌘K now focuses the
// search here.
const MAX_RESULTS = 50;
export function ChatExploreRail({ scope }) {
    return scope ? /*#__PURE__*/ _jsx(RailView, {
        scope: scope,
        source: scope
    }) : /*#__PURE__*/ _jsx(AssistantRail, {});
}
function AssistantRail() {
    const { chats, isLoading, error, refetch } = useChatHistory();
    const shared = useSharedWithMe();
    return /*#__PURE__*/ _jsx(RailView, {
        source: {
            chats,
            isLoading,
            error,
            refetch
        },
        shared: shared
    });
}
function RailView({ scope, source: { chats, isLoading, error, refetch }, shared }) {
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const activeId = scope ? scope.activeChatId : routeId;
    const unbound = !!scope && !scope.projectId;
    const isActive = (chatId)=>!!activeId && chatId === activeId;
    const [tab, setTab] = useState('mine');
    const [query, setQuery] = useState('');
    const searchRef = useRef(null);
    // ⌘/Ctrl+K focuses the rail search.
    useEffect(()=>{
        const onKey = (e)=>{
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                searchRef.current?.focus();
            }
        };
        window.addEventListener('keydown', onKey);
        return ()=>window.removeEventListener('keydown', onKey);
    }, []);
    const results = useMemo(()=>{
        const q = query.trim().toLowerCase();
        return (q ? chats.filter((c)=>c.title.toLowerCase().includes(q)) : chats).slice().sort((a, b)=>(b.updatedAt || b.createdAt).localeCompare(a.updatedAt || a.createdAt)).slice(0, MAX_RESULTS);
    }, [
        chats,
        query
    ]);
    const openChat = (chat)=>{
        if (scope) scope.onSelectChat(chat.chatId);
        else navigate(chatHref(chat));
    };
    return /*#__PURE__*/ _jsxs("aside", {
        "data-testid": scope?.rootTestId ?? Testids.assistant.exploreRail,
        className: "flex h-full w-64 flex-shrink-0 flex-col border-r border-border bg-[var(--surface-rail)]",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "px-4 pt-4 pb-3",
                "data-testid": scope?.newChatTestId,
                children: unbound ? /*#__PURE__*/ _jsx("p", {
                    className: "text-xs text-muted-foreground",
                    children: "No contract project is linked — nothing to chat about here."
                }) : /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": Testids.assistant.newChatButton,
                    disabled: scope?.newChatDisabled,
                    fullWidth: true,
                    iconLeft: /*#__PURE__*/ _jsx(Plus, {
                        "aria-hidden": "true"
                    }),
                    onClick: scope ? scope.onNewChat : ()=>navigate('/assistant'),
                    size: "sm",
                    children: "New chat"
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "px-4 pb-3",
                children: /*#__PURE__*/ _jsx(SpecraSearchField, {
                    ref: searchRef,
                    value: query,
                    onChange: (e)=>setQuery(e.target.value),
                    placeholder: "Search chats",
                    "aria-label": "Search chats",
                    "data-testid": Testids.assistant.exploreSearch,
                    icon: /*#__PURE__*/ _jsx(Search, {
                        className: "h-3.5 w-3.5"
                    }),
                    className: "h-auto bg-muted/40 py-1.5 text-[length:var(--fs-ui-body)]"
                })
            }),
            !scope && shared && /*#__PURE__*/ _jsx("div", {
                role: "tablist",
                "aria-label": "Chat list",
                className: "flex gap-1 px-4 pb-2",
                children: [
                    [
                        'mine',
                        'Mine',
                        Testids.assistant.railTabMine
                    ],
                    [
                        'shared',
                        shared.isLoaded ? `Shared (${shared.total})` : 'Shared',
                        Testids.assistant.railTabShared
                    ]
                ].map(([key, label, testid])=>/*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "ghost",
                        size: "sm",
                        type: "button",
                        role: "tab",
                        id: `chat-rail-tab-${key}`,
                        "aria-selected": tab === key,
                        "aria-controls": "chat-rail-panel",
                        "data-testid": testid,
                        onClick: ()=>setTab(key),
                        className: cn('rounded-md px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', tab === key ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground'),
                        children: label
                    }, key))
            }),
            tab === 'mine' && /*#__PURE__*/ _jsx("div", {
                className: "px-4 pb-1.5",
                children: /*#__PURE__*/ _jsx(SpecraEyebrow, {
                    children: query.trim() ? 'Results' : 'Recent'
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                id: "chat-rail-panel",
                ...scope ? {} : {
                    role: 'tabpanel',
                    'aria-labelledby': `chat-rail-tab-${tab}`
                },
                className: "flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-2.5 pb-4",
                children: tab === 'shared' && shared ? /*#__PURE__*/ _jsx(SharedPanel, {
                    shared: shared,
                    activeId: activeId
                }) : isLoading ? /*#__PURE__*/ _jsx("div", {
                    "data-testid": Testids.assistant.exploreLoading,
                    "aria-busy": "true",
                    className: "flex flex-col gap-1",
                    children: [
                        0,
                        1,
                        2
                    ].map((i)=>/*#__PURE__*/ _jsxs("div", {
                            className: "animate-pulse rounded-md px-3 py-2.5",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "h-3 w-3/4 rounded bg-muted"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "mt-2 h-2.5 w-1/2 rounded bg-muted"
                                })
                            ]
                        }, i))
                }) : error ? /*#__PURE__*/ _jsxs("div", {
                    "data-testid": Testids.assistant.exploreError,
                    className: "flex flex-col items-center gap-2 px-2 py-6 text-center",
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                            children: "Couldn't load chats."
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "ghost",
                            size: "sm",
                            type: "button",
                            "data-testid": Testids.assistant.exploreRetry,
                            onClick: ()=>refetch(),
                            className: "rounded-md border border-border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                            children: "Retry"
                        })
                    ]
                }) : results.length === 0 ? /*#__PURE__*/ _jsx("p", {
                    className: "px-2 py-6 text-center text-[length:var(--fs-ui-body)] text-muted-foreground",
                    children: query.trim() ? 'No chats match your search' : unbound ? 'No chats — no contract project is linked to this program.' : scope ? 'No chats yet. Start one to draft documents, plans, or emails for this program.' : 'No conversations yet'
                }) : results.map((chat)=>/*#__PURE__*/ _jsxs(SpecraButton, {
                        variant: "ghost",
                        size: "sm",
                        type: "button",
                        "data-testid": Testids.assistant.exploreItem,
                        "data-chat-id": chat.chatId,
                        onClick: ()=>openChat(chat),
                        className: cn('block h-auto w-full rounded-md border px-3 py-2.5 text-left transition-colors', isActive(chat.chatId) ? 'border-border-strong bg-card' : 'border-transparent hover:border-border hover:bg-card'),
                        children: [
                            /*#__PURE__*/ _jsxs("span", {
                                className: "flex items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "min-w-0 flex-1 truncate text-[length:var(--fs-ui-body)] font-medium text-foreground",
                                        children: chat.title || 'Untitled chat'
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "flex-shrink-0 text-[length:var(--fs-ui-label)] tabular-nums text-muted-foreground",
                                        children: formatRelativeTime(chat.updatedAt || chat.createdAt)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "mt-[var(--space-0-5)] block truncate text-[length:var(--fs-ui-eyebrow)] text-muted-foreground",
                                children: chat.byline ?? (chat.projectName ? `Project · ${chat.projectName}` : 'Your chat')
                            })
                        ]
                    }, chat.chatId))
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex-shrink-0 border-t border-border px-2.5 py-2",
                children: /*#__PURE__*/ _jsxs(Link, {
                    to: "/history",
                    "data-testid": Testids.assistant.exploreViewAll,
                    "aria-label": scope ? 'All chat history' : `Chat History, ${chats.length} ${chats.length === 1 ? 'chat' : 'chats'}`,
                    className: "flex items-center gap-2 rounded-md px-2.5 py-2 text-[length:var(--fs-ui-body)] font-medium text-foreground transition-colors hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    children: [
                        /*#__PURE__*/ _jsx(History, {
                            className: "h-4 w-4 flex-shrink-0 text-muted-foreground",
                            "aria-hidden": "true"
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "flex-1",
                            children: scope ? 'All chat history' : 'Chat History'
                        }),
                        !scope && /*#__PURE__*/ _jsx("span", {
                            className: "rounded-full bg-muted px-1.5 py-0.5 text-[length:var(--fs-ui-label)] tabular-nums text-muted-foreground",
                            children: chats.length
                        }),
                        /*#__PURE__*/ _jsx(ChevronRight, {
                            className: "h-4 w-4 flex-shrink-0 text-muted-foreground",
                            "aria-hidden": "true"
                        })
                    ]
                })
            })
        ]
    });
}
function SharedPanel({ shared, activeId }) {
    if (shared.isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            "data-testid": Testids.assistant.sharedLoading,
            "aria-busy": "true",
            className: "flex flex-col gap-1",
            children: [
                0,
                1,
                2
            ].map((i)=>/*#__PURE__*/ _jsxs("div", {
                    className: "animate-pulse rounded-md px-3 py-2.5",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "h-3 w-3/4 rounded bg-muted"
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "mt-2 h-2.5 w-1/2 rounded bg-muted"
                        })
                    ]
                }, i))
        });
    }
    if (shared.isError) {
        return /*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.assistant.sharedError,
            className: "flex flex-col items-center gap-2 px-2 py-6 text-center",
            children: [
                /*#__PURE__*/ _jsx("p", {
                    className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                    children: "Couldn't load shared chats."
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "ghost",
                    size: "sm",
                    type: "button",
                    "data-testid": Testids.assistant.sharedRetry,
                    onClick: ()=>shared.refetch(),
                    className: "rounded-md border border-border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    children: "Retry"
                })
            ]
        });
    }
    if (shared.chats.length === 0) {
        return /*#__PURE__*/ _jsx("p", {
            "data-testid": Testids.assistant.sharedEmpty,
            className: "px-2 py-6 text-center text-[length:var(--fs-ui-body)] text-muted-foreground",
            children: "Nothing has been shared with you yet."
        });
    }
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: shared.chats.map((chat)=>/*#__PURE__*/ _jsxs(Link, {
                to: `${chatHref(chat)}?shared=true`,
                "data-testid": Testids.assistant.sharedItem,
                "data-chat-id": chat.chatId,
                className: cn('block w-full rounded-md border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', chat.chatId === activeId ? 'border-border-strong bg-card' : 'border-transparent hover:border-border hover:bg-card'),
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "min-w-0 flex-1 truncate text-[length:var(--fs-ui-body)] font-medium text-foreground",
                                children: chat.title || 'Untitled chat'
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "flex-shrink-0 text-[length:var(--fs-ui-label)] tabular-nums text-muted-foreground",
                                children: formatRelativeTime(chat.sharedAt)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "mt-[var(--space-0-5)] block truncate text-[length:var(--fs-ui-eyebrow)] text-muted-foreground",
                        children: chat.lastMessage?.preview || `Shared by ${chat.sharedBy.name}`
                    })
                ]
            }, chat.chatId))
    });
}
